import React from "react";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "./ui/form";
import { useForm } from "react-hook-form";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ClipLoader } from "react-spinners";
import { useToast } from "./ui/use-toast";
import { Checkbox } from "./ui/checkbox";
import { useEditClientProduct } from "../migration-api/api-hooks/clients";

const ProductForm = ({ clientProduct, isOnboarding = false, onComplete }) => {
	const { toast } = useToast();

	const form = useForm({
		defaultValues: {
			prodName: clientProduct?.prod_name ?? "",
			mandateNo: clientProduct?.nafdac_reg_no ?? "N/A",
			isMandate: clientProduct?.is_nafdac ?? false,
			gtin: clientProduct?.gtin ?? "",
			description: clientProduct?.description ?? "",
			clientEmail: clientProduct?.client_email ?? "",
		},
	});

	// Hook for editing/updating client product
	const { editClientProduct, isLoading: isUpdating } = useEditClientProduct({
		onSuccess: () => {
			toast({
				variant: "success",
				title: "Success",
				description: isOnboarding
					? "Product submitted successfully"
					: "Product updated successfully",
			});

			// Reset form if onboarding
			if (isOnboarding) {
				form.reset({
					prodName: "",
					mandateNo: "",
					isMandate: false,
					gtin: "",
					description: "",
					clientEmail: "",
				});
			}

			// Call onComplete callback if provided
			if (onComplete) {
				onComplete();
			}
		},
		onError: (error) => {
			const errorMessage =
				error?.response?.data?.message ||
				error?.message ||
				"An error occurred";

			toast({
				variant: "destructive",
				title: "Error",
				description: errorMessage,
			});
		},
	});

	function onSubmit(values) {
		const payload = {
			prodName: values.prodName,
			mandateNo: values.mandateNo,
			description: values.description,
			isMandate: values.isMandate,
			gtin: values.gtin,
			clientEmail: values.clientEmail,
		};

		editClientProduct(payload);
	}

	return (
		<div>
			<p className="font-medium text-xl text-primary mb-8">
				{isOnboarding ? "Submit Product" : "Update Product"}
			</p>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-8"
					autoComplete="off"
				>
					{/* Product Name */}
					<FormField
						control={form.control}
						name="prodName"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Product Name</FormLabel>
								<FormControl>
									<Input
										placeholder="Enter product name here"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* NAFDAC Registration Number */}
					<FormField
						control={form.control}
						name="mandateNo"
						render={({ field }) => (
							<FormItem>
								<FormLabel>NAFDAC Reg Number</FormLabel>
								<FormControl>
									<Input
										placeholder="Enter NAFDAC registration number"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* GTIN (disabled) */}
					<FormField
						control={form.control}
						name="gtin"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Client GTIN</FormLabel>
								<FormControl>
									<Input
										placeholder="Enter GTIN"
										{...field}
										disabled
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Product Description */}
					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Product Description</FormLabel>
								<FormControl>
									<Input
										placeholder="Enter product description"
										{...field}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* NAFDAC Mandate Checkbox */}
					<FormField
						control={form.control}
						name="isMandate"
						render={({ field }) => (
							<FormItem className="flex flex-row items-start space-x-3 space-y-0">
								<FormControl>
									<Checkbox
										checked={field.value}
										onCheckedChange={field.onChange}
									/>
								</FormControl>
								<div className="space-y-1 leading-none">
									<FormLabel>NAFDAC Mandate</FormLabel>
									<p className="tracking-wider mt-1 italic leading-[1.3] text-xs">
										This means that NAFDAC will be involved
										in the track and trace of the product.
										Uncheck this box if this product is not
										regulated by NAFDAC traceability
										mandate.
									</p>
								</div>
							</FormItem>
						)}
					/>

					{/* Submit Button */}
					<Button type="submit" disabled={isUpdating}>
						{isUpdating ? (
							<span className="flex items-center gap-2">
								<ClipLoader color="white" size={20} />
								{isOnboarding ? "Submitting..." : "Updating..."}
							</span>
						) : (
							<span>Submit</span>
						)}
					</Button>
				</form>
			</Form>
		</div>
	);
};

export default ProductForm;
